import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import aleData from '../data/aleData.json' with { type: 'json' };
import {
  advanceGauntletPhysics,
  getExistingGauntletRuntime,
  getGauntletRuntime,
  queueTumbler,
  resetGauntletPhysics,
  type GauntletRuntime,
} from '../physics/gauntletPhysics';
import {
  BOTS_PER_LANE,
  COURSE_MESH,
  LANE_COLORS,
  MAX_LANES,
  PITS,
  pitTarget,
  samplePath,
  type PathSample,
} from './gauntletCourse';
import { FS_CELEBRATE_SEC, FS_FLIP_SEC } from './gauntletElimination';
import {
  assignFates,
  assignTerminalBenchFates,
  BASE_PROGRESS_RATE,
  createSolEntry,
  EXAMPLE_TASKS,
  laneId,
  SOL_LANE_ID,
  type BotSim,
  type ExampleTask,
  type LeaderboardEntry,
} from './gauntletSim';
import './Gauntlet.css';

// ---------- data ----------

const ALL_VERIFIED: LeaderboardEntry[] = (aleData.leaderboard as LeaderboardEntry[]).filter(
  (e) => e.verified === true,
);

const SOL_ENTRY = createSolEntry();

const DEFAULT_PICKS = [
  'Codex|GPT-5.5',
  'Claude Code|Fable 5',
  'Gemini CLI|Gemini 3.1 Pro',
] as const;

/** Lane Z including optional Sol (4th) and gotcha offset. */
const EXT_LANE_Z = [-2.4, 0, 2.4, 4.8] as const;
const EXT_LANE_COLORS = [...LANE_COLORS, '#e8c470'] as const;
const GOTCHA_Z = -9.2;

function findEntry(id: string): LeaderboardEntry | undefined {
  if (id === SOL_LANE_ID) return SOL_ENTRY;
  return ALL_VERIFIED.find((e) => laneId(e) === id);
}

function defaultLaneIds(): string[] {
  const picks: string[] = [];
  for (const id of DEFAULT_PICKS) {
    if (findEntry(id)) picks.push(id);
  }
  for (const e of ALL_VERIFIED) {
    if (picks.length >= MAX_LANES) break;
    const id = laneId(e);
    if (!picks.includes(id)) picks.push(id);
  }
  return picks.slice(0, MAX_LANES);
}

// ---------- window bridge ----------

interface GauntletLaneBridge {
  id: string;
  agent: string;
  backbone: string;
  alive: number;
  eliminated: [number, number, number];
  falseSummits: number;
  clinging: number;
  summited: number;
}

interface GauntletState {
  canvasReady: boolean;
  running: boolean;
  seed: number;
  lanes: GauntletLaneBridge[];
  physicsBodyCount: number;
  awakeCount: number;
  speed: number;
  /** false when closed; task id string when open */
  taskCardOpen: false | string;
}

interface GauntletActions {
  start: (laneIds?: string[]) => void;
  reset: () => void;
  setSpeed: (n: number) => void;
  openTaskCard: (botIdx?: number) => void;
}

declare global {
  interface Window {
    __gauntletState?: GauntletState;
    __gauntletActions?: GauntletActions;
  }
}

function emptyLaneBridge(entry: LeaderboardEntry): GauntletLaneBridge {
  return {
    id: laneId(entry),
    agent: entry.agent,
    backbone: entry.backbone,
    alive: BOTS_PER_LANE,
    eliminated: [0, 0, 0],
    falseSummits: 0,
    clinging: 0,
    summited: 0,
  };
}

function writeGauntletState(patch: Partial<GauntletState>) {
  const previous = window.__gauntletState;
  window.__gauntletState = {
    canvasReady: patch.canvasReady ?? previous?.canvasReady ?? false,
    running: patch.running ?? previous?.running ?? false,
    seed: patch.seed ?? previous?.seed ?? 0,
    lanes: patch.lanes ?? previous?.lanes ?? [],
    physicsBodyCount: patch.physicsBodyCount ?? previous?.physicsBodyCount ?? 0,
    awakeCount: patch.awakeCount ?? previous?.awakeCount ?? 0,
    speed: patch.speed ?? previous?.speed ?? 1,
    taskCardOpen:
      patch.taskCardOpen !== undefined
        ? patch.taskCardOpen
        : (previous?.taskCardOpen ?? false),
  };
}

// ---------- shared scratch (no per-frame allocs) ----------

const scratchPath: PathSample = { x: 0, y: 0, z: 0 };
const scratchDummy = new THREE.Object3D();
const scratchQuat = new THREE.Quaternion();
const RED = new THREE.Color('#ff2a2a');
const GREEN_DONE = new THREE.Color('#3dff7a');
const GOLD = new THREE.Color('#e8c470');

// ---------- course geometry ----------

function CourseGeometry({ showGotcha }: { showGotcha: boolean }) {
  const tileMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#3a4254',
        roughness: 0.78,
        metalness: 0.1,
        emissive: '#1a2233',
        emissiveIntensity: 0.22,
      }),
    [],
  );
  const accentMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#4a5468',
        roughness: 0.65,
        metalness: 0.14,
        emissive: '#141c2a',
        emissiveIntensity: 0.18,
      }),
    [],
  );
  const pitMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#12151e',
        roughness: 0.92,
        metalness: 0.05,
        emissive: '#0a0c14',
        emissiveIntensity: 0.15,
      }),
    [],
  );
  /** Per-stage trench accents so heaps read as a bar chart of failures. */
  const pitWallMats = useMemo(
    () => [
      new THREE.MeshStandardMaterial({
        color: '#1a2438',
        roughness: 0.88,
        metalness: 0.08,
        emissive: '#0c1524',
        emissiveIntensity: 0.28,
      }),
      new THREE.MeshStandardMaterial({
        color: '#1e1a28',
        roughness: 0.88,
        metalness: 0.08,
        emissive: '#14101c',
        emissiveIntensity: 0.28,
      }),
      new THREE.MeshStandardMaterial({
        color: '#1a2220',
        roughness: 0.88,
        metalness: 0.08,
        emissive: '#0c1614',
        emissiveIntensity: 0.28,
      }),
    ],
    [],
  );
  const summitMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#e8d4a0',
        roughness: 0.32,
        metalness: 0.45,
        emissive: '#6a5020',
        emissiveIntensity: 0.55,
      }),
    [],
  );
  const laserMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: '#e8f0ff',
        transparent: true,
        opacity: 0.9,
      }),
    [],
  );
  const gotchaMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#2a4038',
        roughness: 0.75,
        metalness: 0.1,
        emissive: '#0a2018',
        emissiveIntensity: 0.25,
      }),
    [],
  );

  useEffect(
    () => () => {
      tileMat.dispose();
      accentMat.dispose();
      pitMat.dispose();
      pitWallMats.forEach((m) => m.dispose());
      summitMat.dispose();
      laserMat.dispose();
      gotchaMat.dispose();
    },
    [tileMat, accentMat, pitMat, pitWallMats, summitMat, laserMat, gotchaMat],
  );

  const m = COURSE_MESH;

  const hurdles = useMemo(() => {
    const list: Array<{ x: number; z: number }> = [];
    for (let i = 0; i < 5; i += 1) {
      list.push({ x: -14.5 + i * 1.6, z: 0 });
    }
    return list;
  }, []);

  return (
    <group>
      {/* Stage 1 flat run */}
      <mesh position={[m.stage1.x, m.stage1.y, m.stage1.z]} material={tileMat} receiveShadow>
        <boxGeometry args={[m.stage1.w, m.stage1.h, m.stage1.d]} />
      </mesh>
      {hurdles.map((h, i) => (
        <mesh key={`hurdle-${i}`} position={[h.x, m.Y_FLOOR + 0.18, h.z]} material={accentMat}>
          <boxGeometry args={[0.18, 0.36, 7.2]} />
        </mesh>
      ))}

      {/* Stage 2 ridge slabs */}
      {[0, 1, 2, 3].map((i) => {
        const x = -2.8 + i * 2.35;
        const y = m.Y_FLOOR + 0.35 + i * 0.55;
        return (
          <mesh key={`ridge-${i}`} position={[x, y, 0]} material={tileMat} receiveShadow>
            <boxGeometry args={[2.1, 0.28, 7.8]} />
          </mesh>
        );
      })}

      {/* Stage 3 cliff wall + cling ledge */}
      <mesh
        position={[m.stage3Wall.x, m.stage3Wall.y, m.stage3Wall.z]}
        material={accentMat}
        castShadow
        receiveShadow
      >
        <boxGeometry args={[m.stage3Wall.w, m.stage3Wall.h, m.stage3Wall.d]} />
      </mesh>
      <mesh position={[m.stage3Ledge.x, m.stage3Ledge.y, m.stage3Ledge.z]} material={tileMat}>
        <boxGeometry args={[m.stage3Ledge.w, m.stage3Ledge.h, m.stage3Ledge.d]} />
      </mesh>

      {/* Summit podium — warm spotlight pool */}
      <mesh position={[m.summit.x, m.summit.y, m.summit.z]} material={summitMat} receiveShadow>
        <boxGeometry args={[m.summit.w, m.summit.h, m.summit.d]} />
      </mesh>
      <mesh position={[m.podium.x, m.podium.y, m.podium.z]} material={summitMat}>
        <boxGeometry args={[m.podium.w, m.podium.h, m.podium.d]} />
      </mesh>
      <pointLight
        position={[m.summit.x, m.Y_SUMMIT + 4.5, 0]}
        intensity={22}
        distance={16}
        color="#ffe9b0"
      />
      <pointLight
        position={[m.summit.x, m.Y_SUMMIT + 2.2, 1.2]}
        intensity={8}
        distance={10}
        color="#ffd890"
      />

      {/* Pit trenches: floor + 4 containment walls per stage (bar-chart heaps). */}
      {PITS.map((pit, i) => {
        const wallMat = pitWallMats[i] ?? pitMat;
        const floorH = 0.32;
        const floorY = pit.y - pit.h / 2 + floorH / 2;
        // LOW visual rim: containment is the tall invisible physics chimney, so
        // the visible wall stays rim-height and heap fill levels read like a
        // bar chart (a 1-layer fill must show; only the rim itself is hidden).
        const wallH = 0.3;
        const wallY = floorY + floorH / 2 + wallH / 2;
        const t = 0.26;
        return (
          <group key={`pit-${i}`}>
            <mesh position={[pit.x, floorY, pit.z]} material={pitMat} receiveShadow>
              <boxGeometry args={[pit.w, floorH, pit.d]} />
            </mesh>
            <mesh position={[pit.x - pit.w / 2 - t / 2, wallY, pit.z]} material={wallMat}>
              <boxGeometry args={[t, wallH, pit.d + t * 2]} />
            </mesh>
            <mesh position={[pit.x + pit.w / 2 + t / 2, wallY, pit.z]} material={wallMat}>
              <boxGeometry args={[t, wallH, pit.d + t * 2]} />
            </mesh>
            <mesh position={[pit.x, wallY, pit.z - pit.d / 2 - t / 2]} material={wallMat}>
              <boxGeometry args={[pit.w + t * 2, wallH, t]} />
            </mesh>
            <mesh position={[pit.x, wallY, pit.z + pit.d / 2 + t / 2]} material={wallMat}>
              <boxGeometry args={[pit.w + t * 2, wallH, t]} />
            </mesh>
          </group>
        );
      })}

      {/* Verifier-gate arches */}
      <GateArch x={m.gate1X} y={m.Y_FLOOR + 1.6} material={laserMat} />
      <GateArch x={m.gate2X} y={m.Y_RIDGE + 1.4} material={laserMat} />

      {/* Subtle void floor under course */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.7, 0]}>
        <planeGeometry args={[48, 28]} />
        <meshBasicMaterial color="#05070c" />
      </mesh>

      {/* Terminal-Bench side-course (gotcha) */}
      {showGotcha && (
        <group position={[0, 0, GOTCHA_Z]}>
          <mesh position={[-4, m.Y_FLOOR / 2, 0]} material={gotchaMat} receiveShadow>
            <boxGeometry args={[22, m.Y_FLOOR, 3.2]} />
          </mesh>
          <mesh position={[8, -0.9, 0]} material={pitMat}>
            <boxGeometry args={[6, 1.2, 3.6]} />
          </mesh>
          <GateArch x={4} y={m.Y_FLOOR + 1.2} material={laserMat} halfW={1.8} height={1.8} />
        </group>
      )}
    </group>
  );
}

function GateArch({
  x,
  y,
  material,
  halfW = 4.1,
  height = 2.4,
}: {
  x: number;
  y: number;
  material: THREE.MeshBasicMaterial;
  halfW?: number;
  height?: number;
}) {
  return (
    <group position={[x, y, 0]}>
      <mesh position={[-halfW, 0, 0]} material={material}>
        <boxGeometry args={[0.06, height, 0.06]} />
      </mesh>
      <mesh position={[halfW, 0, 0]} material={material}>
        <boxGeometry args={[0.06, height, 0.06]} />
      </mesh>
      <mesh position={[0, height / 2, 0]} material={material}>
        <boxGeometry args={[halfW * 2 + 0.06, 0.06, 0.06]} />
      </mesh>
      <mesh position={[0, 0.1, 0]}>
        <boxGeometry args={[0.04, height * 0.85, halfW * 2]} />
        <meshBasicMaterial color="#b8d4ff" transparent opacity={0.14} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ---------- lane runtime ----------

interface LaneRuntime {
  entry: LeaderboardEntry;
  color: string;
  laneIndex: number;
  bots: BotSim[];
  alive: number;
  eliminated: [number, number, number];
  falseSummits: number;
  clinging: number;
  summited: number;
  /** Side-course (Terminal-Bench) flag */
  isGotcha?: boolean;
  zOffset: number;
}

function createLaneRuntime(
  entry: LeaderboardEntry,
  laneIndex: number,
  seed: number,
  opts?: { gotcha?: boolean; zOffset?: number },
): LaneRuntime {
  const bots = opts?.gotcha
    ? assignTerminalBenchFates(entry, seed)
    : assignFates(entry, seed);
  return {
    entry,
    color: entry.isSol
      ? EXT_LANE_COLORS[3]
      : EXT_LANE_COLORS[laneIndex % EXT_LANE_COLORS.length],
    laneIndex,
    bots,
    alive: BOTS_PER_LANE,
    eliminated: [0, 0, 0],
    falseSummits: 0,
    clinging: 0,
    summited: 0,
    isGotcha: opts?.gotcha,
    zOffset: opts?.zOffset ?? 0,
  };
}

type CountsCb = (
  laneIndex: number,
  alive: number,
  elim: [number, number, number],
  clinging: number,
  falseSummits: number,
) => void;

function beginPhysicsElim(
  bot: BotSim,
  laneIndex: number,
  botIndex: number,
  stage: 0 | 1 | 2,
  physicsRef: MutableRefObject<GauntletRuntime | null>,
) {
  if (bot.physicsSpawned) return;
  const rt = physicsRef.current;
  if (!rt) return;
  bot.physicsSpawned = true;
  const fateStage = (stage === 0 || stage === 1 || stage === 2 ? stage : 0) as 0 | 1 | 2;
  // Drop-in handoff: the kinematic elimination animation visually carries the
  // bot from its elimination point to the pit; the dynamic body then
  // materializes just above the pit's (invisible) containment chimney and
  // drops straight in. Ballistic lobs into the narrow column missed too often
  // and fell out of the world — dropping in guarantees capture while physics
  // still owns the landing, tumble, and stack that forms the heap.
  const pit = pitTarget(fateStage, bot.startZ, botIndex);
  const dropY = 3.1 + (botIndex % 6) * 0.22;
  queueTumbler(rt, {
    x: pit.x,
    y: dropY,
    z: pit.z,
    ix: ((botIndex % 7) - 3) * 0.02,
    iy: -0.15,
    iz: ((botIndex % 5) - 2) * 0.02,
    laneIndex,
    botIndex,
  });
}

// ---------- lane runners ----------

function LaneRunners({
  lane,
  runningRef,
  speedRef,
  physicsRef,
  onCounts,
  markerMeshRef,
}: {
  lane: LaneRuntime;
  runningRef: MutableRefObject<boolean>;
  speedRef: MutableRefObject<number>;
  physicsRef: MutableRefObject<GauntletRuntime | null>;
  onCounts: CountsCb;
  markerMeshRef: MutableRefObject<THREE.InstancedMesh | null>;
}) {
  const meshRef = useRef<THREE.InstancedMesh | null>(null);
  const laneRef = useRef(lane);
  laneRef.current = lane;
  const laneColor = useMemo(() => new THREE.Color(lane.color), [lane.color]);
  const countsDirty = useRef(true);
  const hopAmp = 0.09;
  const isSol = !!lane.entry.isSol;

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const lz = (EXT_LANE_Z[lane.laneIndex] ?? 0) + lane.zOffset;
    for (let i = 0; i < BOTS_PER_LANE; i += 1) {
      const col = i % 10;
      const row = (i / 10) | 0;
      scratchDummy.position.set(
        COURSE_MESH.startX - 0.8 - row * 0.22,
        COURSE_MESH.Y_FLOOR + 0.16,
        lz + (col - 4.5) * 0.16,
      );
      scratchDummy.rotation.set(0, 0, 0);
      scratchDummy.scale.setScalar(1);
      scratchDummy.updateMatrix();
      mesh.setMatrixAt(i, scratchDummy.matrix);
      mesh.setColorAt(i, isSol ? GOLD : laneColor);
      const bot = lane.bots[i];
      bot.status = 'waiting';
      bot.progress = 0;
      bot.elimT = 0;
      bot.fsT = 0;
      bot.physicsSpawned = false;
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    lane.alive = BOTS_PER_LANE;
    lane.eliminated = [0, 0, 0];
    lane.falseSummits = 0;
    lane.clinging = 0;
    lane.summited = 0;
    countsDirty.current = true;
  }, [lane, laneColor, isSol]);

  useFrame((_, delta) => {
    const mesh = meshRef.current;
    const rt = laneRef.current;
    if (!mesh) return;

    const dt = Math.min(delta, 0.05) * speedRef.current;
    const lz = (EXT_LANE_Z[rt.laneIndex] ?? 0) + rt.zOffset;
    const running = runningRef.current;
    const markers = markerMeshRef.current;

    for (let i = 0; i < BOTS_PER_LANE; i += 1) {
      const bot = rt.bots[i];

      // Marker default: hidden
      if (markers) {
        scratchDummy.position.set(0, -50, 0);
        scratchDummy.scale.setScalar(0.001);
        scratchDummy.updateMatrix();
        markers.setMatrixAt(i, scratchDummy.matrix);
      }

      if (bot.status === 'waiting') {
        if (running) {
          bot.status = 'running';
          bot.progress = 0;
        }
        const hop = Math.abs(Math.sin(performance.now() * 0.004 + bot.phase)) * hopAmp * 0.4;
        scratchDummy.position.set(
          COURSE_MESH.startX - 0.8 - ((i / 10) | 0) * 0.22,
          COURSE_MESH.Y_FLOOR + 0.16 + hop,
          lz + ((i % 10) - 4.5) * 0.16,
        );
        scratchDummy.scale.setScalar(1);
        scratchDummy.rotation.set(0, Math.PI / 2, 0);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
        continue;
      }

      if (bot.status === 'eliminated' || bot.status === 'done') {
        // Late physics handoff if runtime was not ready at the fail frame
        if (
          bot.status === 'eliminated' &&
          !bot.physicsSpawned &&
          bot.fate !== 3 &&
          bot.fate !== 4
        ) {
          beginPhysicsElim(bot, rt.laneIndex, i, bot.fate as 0 | 1 | 2, physicsRef);
        }
        scratchDummy.position.set(0, -40, 0);
        scratchDummy.scale.setScalar(0.001);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
        continue;
      }

      if (bot.status === 'clinging') {
        samplePath(bot.failAt, lz, scratchPath);
        const sway = Math.sin(performance.now() * 0.003 + bot.phase) * 0.03;
        scratchDummy.position.set(
          scratchPath.x + 0.15,
          scratchPath.y + 0.2 + sway * 0.2,
          scratchPath.z + sway,
        );
        scratchDummy.scale.setScalar(1);
        scratchDummy.rotation.set(0, Math.PI / 2, 0);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
        continue;
      }

      if (bot.status === 'idle_no_data') {
        samplePath(bot.failAt, lz, scratchPath);
        const hop = Math.abs(Math.sin(performance.now() * 0.003 + bot.phase)) * hopAmp * 0.3;
        scratchDummy.position.set(scratchPath.x, scratchPath.y + 0.16 + hop, scratchPath.z);
        scratchDummy.scale.setScalar(1);
        scratchDummy.rotation.set(0, Math.PI / 2, 0);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
        continue;
      }

      if (bot.status === 'false_summit' || bot.status === 'false_summit_flip') {
        bot.fsT += dt;
        samplePath(bot.failAt, lz, scratchPath);
        // Bot stands still
        scratchDummy.position.set(scratchPath.x, scratchPath.y + 0.16, scratchPath.z);
        scratchDummy.scale.setScalar(1);
        scratchDummy.rotation.set(0, Math.PI / 2, 0);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);

        // Green DONE marker → red ✗ after celebrate
        if (markers) {
          const celebrating = bot.status === 'false_summit';
          const markerY = scratchPath.y + 0.55 + (celebrating ? Math.sin(bot.fsT * 8) * 0.04 : 0);
          scratchDummy.position.set(scratchPath.x, markerY, scratchPath.z);
          scratchDummy.scale.setScalar(celebrating ? 0.55 : 0.5);
          scratchDummy.rotation.set(0, bot.fsT * 2, 0);
          scratchDummy.updateMatrix();
          markers.setMatrixAt(i, scratchDummy.matrix);
          markers.setColorAt(i, celebrating ? GREEN_DONE : RED);
        }

        if (bot.status === 'false_summit' && bot.fsT >= FS_CELEBRATE_SEC) {
          bot.status = 'false_summit_flip';
          bot.fsT = 0;
          mesh.setColorAt(i, RED);
        } else if (bot.status === 'false_summit_flip' && bot.fsT >= FS_FLIP_SEC) {
          // Trapdoor → physics tumble
          bot.startX = scratchPath.x;
          bot.startY = scratchPath.y + 0.16;
          bot.startZ = scratchPath.z;
          const pit = pitTarget(bot.fate as 0 | 1 | 2, lz, i);
          bot.pitX = pit.x;
          bot.pitY = pit.y;
          bot.pitZ = pit.z;
          beginPhysicsElim(bot, rt.laneIndex, i, bot.fate as 0 | 1 | 2, physicsRef);
          bot.status = 'eliminated';
          countsDirty.current = true;
          if (markers) {
            scratchDummy.position.set(0, -50, 0);
            scratchDummy.scale.setScalar(0.001);
            scratchDummy.updateMatrix();
            markers.setMatrixAt(i, scratchDummy.matrix);
          }
        }
        continue;
      }

      if (bot.status === 'eliminating') {
        // One-frame handoff residual — hide immediately after queue
        bot.elimT += dt;
        scratchDummy.position.set(0, -40, 0);
        scratchDummy.scale.setScalar(0.001);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
        if (bot.elimT > 0.05) {
          bot.status = 'eliminated';
          countsDirty.current = true;
        }
        continue;
      }

      if (bot.status === 'running') {
        bot.progress += BASE_PROGRESS_RATE * bot.speedJitter * dt * (rt.isGotcha ? 1.35 : 1);

        if (bot.progress >= bot.failAt) {
          if (bot.fate === 4) {
            bot.status = 'idle_no_data';
            bot.progress = bot.failAt;
            countsDirty.current = true;
            continue;
          }
          if (bot.fate === 3) {
            if (rt.isGotcha) {
              bot.status = 'done';
              countsDirty.current = true;
              continue;
            }
            bot.status = 'clinging';
            bot.progress = bot.failAt;
            countsDirty.current = true;
            continue;
          }

          // False-summit branch
          if (bot.isFalseSummit && (bot.fate === 0 || bot.fate === 1)) {
            bot.status = 'false_summit';
            bot.fsT = 0;
            bot.progress = bot.failAt;
            mesh.setColorAt(i, GREEN_DONE);
            countsDirty.current = true;
            continue;
          }

          // Regular elimination → physics
          if (rt.isGotcha) {
            samplePath(bot.failAt, lz, scratchPath);
            bot.startX = scratchPath.x;
            bot.startY = scratchPath.y + 0.16;
            bot.startZ = scratchPath.z;
          } else {
            samplePath(bot.failAt, lz, scratchPath);
            bot.startX = scratchPath.x;
            bot.startY = scratchPath.y + 0.16;
            bot.startZ = scratchPath.z;
          }
          const stage = (bot.fate === 0 || bot.fate === 1 || bot.fate === 2 ? bot.fate : 0) as
            | 0
            | 1
            | 2;
          const pit = pitTarget(stage, lz, i);
          bot.pitX = pit.x;
          bot.pitY = pit.y;
          bot.pitZ = pit.z;
          bot.elimT = 0;
          beginPhysicsElim(bot, rt.laneIndex, i, stage, physicsRef);
          bot.status = 'eliminating';
          mesh.setColorAt(i, RED);
          countsDirty.current = true;
          continue;
        }

        // Hop-jog
        if (rt.isGotcha) {
          // Short linear side-course
          const gx = COURSE_MESH.startX + bot.progress * 28;
          const hop = Math.abs(Math.sin(bot.progress * 55 + bot.phase)) * hopAmp;
          scratchDummy.position.set(gx, COURSE_MESH.Y_FLOOR + 0.16 + hop, lz);
        } else {
          samplePath(bot.progress, lz, scratchPath);
          const hop = Math.abs(Math.sin(bot.progress * 55 + bot.phase)) * hopAmp;
          const weave = Math.sin(bot.progress * 18 + bot.phase) * 0.12;
          scratchDummy.position.set(
            scratchPath.x,
            scratchPath.y + 0.16 + hop,
            scratchPath.z + weave,
          );
        }
        scratchDummy.scale.setScalar(1);
        scratchDummy.rotation.set(
          0,
          Math.PI / 2,
          Math.sin(bot.progress * 55 + bot.phase) * 0.15,
        );
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
      }
    }

    // Recount
    let e0 = 0;
    let e1 = 0;
    let e2 = 0;
    let cl = 0;
    let al = 0;
    let fs = 0;
    for (let i = 0; i < BOTS_PER_LANE; i += 1) {
      const bot = rt.bots[i];
      if (
        bot.status === 'eliminated' ||
        bot.status === 'eliminating' ||
        bot.status === 'false_summit' ||
        bot.status === 'false_summit_flip'
      ) {
        if (bot.fate === 0) e0 += 1;
        else if (bot.fate === 1) e1 += 1;
        else if (bot.fate === 2) e2 += 1;
        if (bot.isFalseSummit) fs += 1;
      } else if (bot.status === 'clinging') {
        cl += 1;
        al += 1;
      } else if (
        bot.status === 'running' ||
        bot.status === 'waiting' ||
        bot.status === 'idle_no_data' ||
        bot.status === 'done'
      ) {
        al += 1;
      }
    }

    if (
      e0 !== rt.eliminated[0] ||
      e1 !== rt.eliminated[1] ||
      e2 !== rt.eliminated[2] ||
      al !== rt.alive ||
      cl !== rt.clinging ||
      fs !== rt.falseSummits
    ) {
      rt.eliminated = [e0, e1, e2];
      rt.alive = al;
      rt.clinging = cl;
      rt.falseSummits = fs;
      countsDirty.current = true;
    }

    if (countsDirty.current) {
      countsDirty.current = false;
      onCounts(rt.laneIndex, rt.alive, rt.eliminated, rt.clinging, rt.falseSummits);
    }

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    if (markers) {
      markers.instanceMatrix.needsUpdate = true;
      if (markers.instanceColor) markers.instanceColor.needsUpdate = true;
    }
  });

  return (
    <>
      <instancedMesh
        ref={meshRef}
        args={[undefined, undefined, BOTS_PER_LANE]}
        castShadow
        frustumCulled={false}
      >
        <boxGeometry args={[0.18, 0.32, 0.18]} />
        {/* DO NOT set vertexColors — setColorAt requires material without vertexColors:true */}
        <meshStandardMaterial
          color="#ffffff"
          roughness={0.45}
          metalness={isSol ? 0.55 : 0.25}
          emissive={isSol ? '#3a2a08' : '#111111'}
          emissiveIntensity={isSol ? 0.55 : 0.4}
        />
      </instancedMesh>
      {/* False-summit markers (green DONE / red X proxy cubes) */}
      <instancedMesh
        ref={markerMeshRef}
        args={[undefined, undefined, BOTS_PER_LANE]}
        frustumCulled={false}
      >
        <boxGeometry args={[0.22, 0.14, 0.06]} />
        <meshStandardMaterial
          color="#ffffff"
          roughness={0.4}
          metalness={0.2}
          emissive="#222222"
          emissiveIntensity={0.6}
        />
      </instancedMesh>
    </>
  );
}

// ---------- physics tumbler meshes ----------

function TumblerMeshes({
  laneCount,
  physicsRef,
  colors,
  meshRefs,
  onTumblerClick,
}: {
  laneCount: number;
  physicsRef: MutableRefObject<GauntletRuntime | null>;
  colors: string[];
  meshRefs: MutableRefObject<Array<THREE.InstancedMesh | null>>;
  onTumblerClick: (laneIndex: number, instanceId: number) => void;
}) {
  const mats = useMemo(
    () =>
      colors.map(
        (c) =>
          new THREE.MeshStandardMaterial({
            color: c,
            roughness: 0.5,
            metalness: 0.22,
            emissive: '#0a0a0a',
            emissiveIntensity: 0.25,
          }),
      ),
    [colors],
  );

  useEffect(
    () => () => {
      mats.forEach((m) => m.dispose());
    },
    [mats],
  );

  // DEV-gated debug bridge: dump live tumbler positions for automated probes.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as Record<string, unknown>).__gauntletDebug = {
      tumblers: () => {
        const rt = physicsRef.current;
        if (!rt) return [];
        return rt.tumblerBodies.map((_, i) => {
          const off = i * 7;
          return {
            lane: rt.tumblerLane[i],
            bot: rt.tumblerBot[i],
            x: Number(rt.transforms[off]?.toFixed(2)),
            y: Number(rt.transforms[off + 1]?.toFixed(2)),
            z: Number(rt.transforms[off + 2]?.toFixed(2)),
          };
        });
      },
    };
  }, [physicsRef]);

  // Pre-seed instance colors once
  useEffect(() => {
    for (let li = 0; li < laneCount; li += 1) {
      const mesh = meshRefs.current[li];
      if (!mesh) continue;
      const col = new THREE.Color(colors[li] ?? '#888');
      for (let i = 0; i < BOTS_PER_LANE; i += 1) {
        scratchDummy.position.set(0, -80, 0);
        scratchDummy.scale.setScalar(0.001);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
        mesh.setColorAt(i, col);
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.count = 0;
    }
  }, [laneCount, colors, meshRefs]);

  useFrame(() => {
    const rt = physicsRef.current;
    if (!rt) return;

    // Reset counts
    const perLaneCounts = new Int32Array(laneCount);
    for (let li = 0; li < laneCount; li += 1) {
      const mesh = meshRefs.current[li];
      if (!mesh) continue;
      // Hide all then fill from physics
      for (let i = 0; i < BOTS_PER_LANE; i += 1) {
        scratchDummy.position.set(0, -80, 0);
        scratchDummy.scale.setScalar(0.001);
        scratchDummy.updateMatrix();
        mesh.setMatrixAt(i, scratchDummy.matrix);
      }
    }

    const n = rt.tumblerBodies.length;
    for (let i = 0; i < n; i += 1) {
      const li = rt.tumblerLane[i] ?? 0;
      if (li < 0 || li >= laneCount) continue;
      const mesh = meshRefs.current[li];
      if (!mesh) continue;
      const slot = perLaneCounts[li];
      if (slot >= BOTS_PER_LANE) continue;
      perLaneCounts[li] = slot + 1;

      const off = i * 7;
      const px = rt.transforms[off];
      const py = rt.transforms[off + 1];
      const pz = rt.transforms[off + 2];
      const qx = rt.transforms[off + 3];
      const qy = rt.transforms[off + 4];
      const qz = rt.transforms[off + 5];
      const qw = rt.transforms[off + 6];
      scratchDummy.position.set(px, py, pz);
      scratchQuat.set(qx, qy, qz, qw);
      scratchDummy.quaternion.copy(scratchQuat);
      scratchDummy.scale.setScalar(1);
      scratchDummy.updateMatrix();
      mesh.setMatrixAt(slot, scratchDummy.matrix);
    }

    for (let li = 0; li < laneCount; li += 1) {
      const mesh = meshRefs.current[li];
      if (!mesh) continue;
      mesh.count = perLaneCounts[li];
      mesh.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <>
      {Array.from({ length: laneCount }, (_, li) => (
        <instancedMesh
          key={`tumbler-${li}`}
          ref={(el) => {
            meshRefs.current[li] = el;
          }}
          args={[undefined, undefined, BOTS_PER_LANE]}
          castShadow
          frustumCulled={false}
          onClick={(e: ThreeEvent<MouseEvent>) => {
            e.stopPropagation();
            const id = e.instanceId;
            if (id == null) return;
            onTumblerClick(li, id);
          }}
        >
          <boxGeometry args={[0.18, 0.32, 0.18]} />
          <primitive object={mats[li] ?? mats[0]} attach="material" />
        </instancedMesh>
      ))}
    </>
  );
}

// ---------- physics driver ----------

function PhysicsDriver({
  physicsRef,
  speedRef,
  onPhysicsStats,
}: {
  physicsRef: MutableRefObject<GauntletRuntime | null>;
  speedRef: MutableRefObject<number>;
  onPhysicsStats: (bodies: number, awake: number) => void;
}) {
  const lastReport = useRef(0);
  useFrame((_, delta) => {
    const rt = physicsRef.current;
    if (!rt) return;
    const dt = Math.min(delta, 0.05) * speedRef.current;
    advanceGauntletPhysics(rt, dt);
    const now = performance.now();
    if (now - lastReport.current > 100) {
      lastReport.current = now;
      onPhysicsStats(rt.tumblerBodies.length, rt.awakeCount);
    }
  });
  return null;
}

// ---------- canvas ready + click ----------

function SceneBootstrap({ onReady }: { onReady: () => void }) {
  const readyRef = useRef(false);
  useFrame(() => {
    if (!readyRef.current) {
      readyRef.current = true;
      onReady();
    }
  });
  return null;
}

// ---------- scene shell ----------

function GauntletScene({
  lanes,
  runningRef,
  speedRef,
  physicsRef,
  showGotcha,
  onCounts,
  onCanvasReady,
  onPhysicsStats,
  onTumblerClick,
  tumblerMeshRefs,
}: {
  lanes: LaneRuntime[];
  runningRef: MutableRefObject<boolean>;
  speedRef: MutableRefObject<number>;
  physicsRef: MutableRefObject<GauntletRuntime | null>;
  showGotcha: boolean;
  onCounts: CountsCb;
  onCanvasReady: () => void;
  onPhysicsStats: (bodies: number, awake: number) => void;
  onTumblerClick: (laneIndex: number, instanceId: number) => void;
  tumblerMeshRefs: MutableRefObject<Array<THREE.InstancedMesh | null>>;
}) {
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const markerRefs = useRef<Array<MutableRefObject<THREE.InstancedMesh | null>>>(
    lanes.map(() => ({ current: null })),
  );
  // Keep marker ref array length in sync without assigning inside useMemo
  useEffect(() => {
    while (markerRefs.current.length < lanes.length) {
      markerRefs.current.push({ current: null });
    }
  }, [lanes.length]);

  const colors = lanes.map((l) => l.color);
  const { camera } = useThree();

  // Default framing: course fills ~80% width, three-quarter side-on
  useEffect(() => {
    // Wide enough that the full course (x≈±13) AND the three camera-forward
    // pit trenches (z=4.6, x∈[-8.6, 12.7]) sit inside a 1280×720 view.
    camera.position.set(0.5, 10.5, 21);
    camera.lookAt(0.5, 1.8, 1.0);
    if (controlsRef.current) {
      controlsRef.current.target.set(0.5, 1.8, 1.0);
      controlsRef.current.update();
    }
  }, [camera]);

  // Widen camera when gotcha side-course is shown
  useEffect(() => {
    if (!controlsRef.current) return;
    if (showGotcha) {
      camera.position.set(0.5, 12.5, 26);
      controlsRef.current.target.set(0.2, 1.6, -1.5);
    } else {
      camera.position.set(0.5, 10.5, 21);
      controlsRef.current.target.set(0.5, 1.8, 1.0);
    }
    controlsRef.current.update();
  }, [showGotcha, camera]);

  return (
    <>
      <color attach="background" args={['#05070d']} />
      <fogExp2 attach="fog" args={['#05070d', 0.012]} />
      {/* Brighter key + cool fill — lift the COURSE, keep dark void */}
      <ambientLight intensity={0.42} />
      <hemisphereLight args={['#9ab0d0', '#1a1420', 0.35]} />
      <directionalLight
        position={[10, 16, 8]}
        intensity={1.55}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <directionalLight position={[-8, 8, -5]} intensity={0.45} color="#8aa8d8" />
      <directionalLight position={[0, 4, 12]} intensity={0.28} color="#c8d4e8" />

      <CourseGeometry showGotcha={showGotcha} />
      <SceneBootstrap onReady={onCanvasReady} />
      <PhysicsDriver
        physicsRef={physicsRef}
        speedRef={speedRef}
        onPhysicsStats={onPhysicsStats}
      />

      {lanes.map((lane, i) => {
        if (!markerRefs.current[i]) {
          markerRefs.current[i] = { current: null };
        }
        return (
          <LaneRunners
            key={
              (lane.isGotcha ? 'gotcha-' : '') +
              lane.entry.agent +
              lane.entry.backbone +
              lane.laneIndex
            }
            lane={lane}
            runningRef={runningRef}
            speedRef={speedRef}
            physicsRef={physicsRef}
            onCounts={onCounts}
            markerMeshRef={markerRefs.current[i]}
          />
        );
      })}

      <TumblerMeshes
        laneCount={Math.max(lanes.filter((l) => !l.isGotcha).length, 1)}
        physicsRef={physicsRef}
        colors={colors.filter((_, i) => !lanes[i]?.isGotcha)}
        meshRefs={tumblerMeshRefs}
        onTumblerClick={onTumblerClick}
      />

      <OrbitControls
        ref={controlsRef}
        enableDamping
        dampingFactor={0.08}
        target={[0.5, 1.8, 1.0]}
        minDistance={7}
        maxDistance={48}
        maxPolarAngle={Math.PI * 0.48}
      />
    </>
  );
}

// ---------- HUD helpers ----------

function formatTimeScale(raw: string): string {
  if (raw.includes('days to weeks')) return 'days–weeks';
  if (raw.includes('hours to days')) return 'hours–days';
  return raw;
}

function formatRate(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v}%`;
}

// ---------- main page ----------

function Gauntlet({ onBack }: { onBack: () => void }) {
  const [seed, setSeed] = useState(() => (Math.random() * 1e9) | 0);
  const [speed, setSpeed] = useState(1);
  const [running, setRunning] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [laneIds, setLaneIds] = useState<string[]>(() => defaultLaneIds());
  const [hudLanes, setHudLanes] = useState<GauntletLaneBridge[]>([]);
  const [resetKey, setResetKey] = useState(0);
  const [runCompleted, setRunCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showCaveats, setShowCaveats] = useState(false);
  const [showGotcha, setShowGotcha] = useState(false);
  const [solAdded, setSolAdded] = useState(false);
  const [taskCard, setTaskCard] = useState<ExampleTask | null>(null);
  const [physicsBodies, setPhysicsBodies] = useState(0);
  const [physicsAwake, setPhysicsAwake] = useState(0);

  const runningRef = useRef(false);
  const speedRef = useRef(1);
  const lanesRef = useRef<LaneRuntime[]>([]);
  const hudRef = useRef<GauntletLaneBridge[]>([]);
  const physicsRef = useRef<GauntletRuntime | null>(null);
  const tumblerMeshRefs = useRef<Array<THREE.InstancedMesh | null>>([]);
  const hadRunningRef = useRef(false);
  /** Delay results overlay so piles get a beat after the run clears. */
  const resultsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const RESULTS_DELAY_MS = 2800;

  // Rebuild lane runtimes when picks / seed / reset / gotcha change
  const lanes = useMemo(() => {
    const list: LaneRuntime[] = [];
    laneIds.forEach((id, index) => {
      const entry = findEntry(id);
      if (!entry) return;
      list.push(createLaneRuntime(entry, index, seed));
    });
    // Terminal-Bench side-course for first selected model
    if (showGotcha && list.length > 0) {
      const src = list[0];
      list.push(
        createLaneRuntime(src.entry, list.length, seed ^ 0x51a1, {
          gotcha: true,
          zOffset: GOTCHA_Z,
        }),
      );
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneIds, seed, resetKey, showGotcha]);

  // Sync refs to COMMITTED lanes only (never inside useMemo — StrictMode safe).
  useEffect(() => {
    lanesRef.current = lanes;
    const bridges = lanes
      .filter((l) => !l.isGotcha)
      .map((l) => emptyLaneBridge(l.entry));
    hudRef.current = bridges;
    setHudLanes(bridges);
  }, [lanes]);

  useEffect(() => {
    runningRef.current = running;
    writeGauntletState({ running });
    if (running) hadRunningRef.current = true;
  }, [running]);

  useEffect(() => {
    speedRef.current = speed;
    writeGauntletState({ speed });
  }, [speed]);

  useEffect(() => {
    writeGauntletState({
      seed,
      lanes: hudLanes,
      physicsBodyCount: physicsBodies,
      awakeCount: physicsAwake,
    });
  }, [seed, hudLanes, physicsBodies, physicsAwake]);

  useEffect(() => {
    writeGauntletState({ taskCardOpen: taskCard ? taskCard.id : false });
  }, [taskCard]);

  const onCounts = useCallback(
    (
      laneIndex: number,
      alive: number,
      elim: [number, number, number],
      clinging: number,
      falseSummits: number,
    ) => {
      // Gotcha lane uses index past main lanes — only update main HUD
      const mainCount = lanesRef.current.filter((l) => !l.isGotcha).length;
      if (laneIndex >= mainCount) {
        // Still check terminal for gotcha-inclusive all-terminal
      } else {
        const prev = hudRef.current;
        const next = prev.map((lane, i) => {
          if (i !== laneIndex) return lane;
          return {
            ...lane,
            alive,
            eliminated: elim,
            clinging,
            summited: 0,
            falseSummits,
          };
        });
        hudRef.current = next;
        setHudLanes(next);
        writeGauntletState({
          lanes: next,
          running: runningRef.current,
          seed,
          speed: speedRef.current,
          physicsBodyCount: physicsRef.current?.tumblerBodies.length ?? 0,
          awakeCount: physicsRef.current?.awakeCount ?? 0,
        });
      }

      // Auto-clear running when every bot in every active lane is terminal.
      let runningNow = runningRef.current;
      if (runningNow) {
        const allTerminal = lanesRef.current.every((lane) =>
          lane.bots.every(
            (b) =>
              b.status === 'eliminated' ||
              b.status === 'clinging' ||
              b.status === 'done' ||
              b.status === 'idle_no_data',
          ),
        );
        if (allTerminal) {
          runningNow = false;
          runningRef.current = false;
          setRunning(false);
          if (hadRunningRef.current) {
            setRunCompleted(true);
            // Let the failure heaps own the frame before the overlay steals it.
            if (resultsTimerRef.current) clearTimeout(resultsTimerRef.current);
            resultsTimerRef.current = setTimeout(() => {
              resultsTimerRef.current = null;
              setShowResults(true);
            }, RESULTS_DELAY_MS);
          }
        }
      }

      if (!runningNow) {
        writeGauntletState({ running: false });
      }
    },
    [seed],
  );

  const handleCanvasReady = useCallback(() => {
    writeGauntletState({ canvasReady: true });
  }, []);

  const handlePhysicsStats = useCallback((bodies: number, awake: number) => {
    setPhysicsBodies(bodies);
    setPhysicsAwake(awake);
    writeGauntletState({ physicsBodyCount: bodies, awakeCount: awake });
  }, []);

  const start = useCallback(
    async (ids?: string[]) => {
      if (ids && ids.length > 0) {
        const resolved = ids
          .map((id) => findEntry(id))
          .filter((e): e is LeaderboardEntry => !!e)
          .slice(0, MAX_LANES + (solAdded ? 1 : 0));
        if (resolved.length > 0) {
          setLaneIds(resolved.map((e) => laneId(e)).slice(0, solAdded ? 4 : MAX_LANES));
        }
      }
      // Lazy-init physics on first run start — must be ready before bots eliminate
      try {
        const rt = await getGauntletRuntime();
        physicsRef.current = rt;
      } catch (err) {
        console.error('Gauntlet physics init failed', err);
      }
      if (resultsTimerRef.current) {
        clearTimeout(resultsTimerRef.current);
        resultsTimerRef.current = null;
      }
      setShowResults(false);
      setRunning(true);
      runningRef.current = true;
      hadRunningRef.current = true;
      writeGauntletState({ running: true });
    },
    [solAdded],
  );

  const reset = useCallback(() => {
    setRunning(false);
    runningRef.current = false;
    setSeed((Math.random() * 1e9) | 0);
    setResetKey((k) => k + 1);
    if (resultsTimerRef.current) {
      clearTimeout(resultsTimerRef.current);
      resultsTimerRef.current = null;
    }
    setShowResults(false);
    setTaskCard(null);
    const rt = physicsRef.current ?? getExistingGauntletRuntime();
    if (rt) resetGauntletPhysics(rt);
    setPhysicsBodies(0);
    setPhysicsAwake(0);
    writeGauntletState({ running: false, physicsBodyCount: 0, awakeCount: 0, taskCardOpen: false });
  }, []);

  const setSpeedAction = useCallback((n: number) => {
    const next = n === 4 || n === 2 || n === 1 ? n : 1;
    setSpeed(next);
    speedRef.current = next;
    writeGauntletState({ speed: next });
  }, []);

  const openTaskCard = useCallback((botIdx?: number) => {
    // Programmatic open for E2E — pick task from first lane bot or index
    const mainLanes = lanesRef.current.filter((l) => !l.isGotcha);
    const lane = mainLanes[0];
    if (!lane) {
      const fallback = EXAMPLE_TASKS[0];
      setTaskCard(fallback);
      writeGauntletState({ taskCardOpen: fallback.id });
      return;
    }
    const idx =
      botIdx != null
        ? Math.max(0, Math.min(BOTS_PER_LANE - 1, botIdx))
        : 0;
    // Prefer a tumbled bot if available
    let bot = lane.bots[idx];
    if (botIdx == null) {
      const tumbled = lane.bots.find((b) => b.status === 'eliminated' || b.physicsSpawned);
      if (tumbled) bot = tumbled;
    }
    const task = EXAMPLE_TASKS[bot?.taskIndex ?? 0] ?? EXAMPLE_TASKS[0];
    setTaskCard(task);
    writeGauntletState({ taskCardOpen: task.id });
  }, []);

  const onTumblerClick = useCallback((laneIndex: number, instanceId: number) => {
    const rt = physicsRef.current;
    if (!rt) {
      openTaskCard(instanceId);
      return;
    }
    // Map visual instance slot back to body → bot
    let slot = 0;
    for (let i = 0; i < rt.tumblerBodies.length; i += 1) {
      if (rt.tumblerLane[i] !== laneIndex) continue;
      if (slot === instanceId) {
        const botIndex = rt.tumblerBot[i];
        const lane = lanesRef.current.filter((l) => !l.isGotcha)[laneIndex];
        const bot = lane?.bots[botIndex];
        const task = EXAMPLE_TASKS[bot?.taskIndex ?? 0] ?? EXAMPLE_TASKS[0];
        setTaskCard(task);
        writeGauntletState({ taskCardOpen: task.id });
        return;
      }
      slot += 1;
    }
    openTaskCard(instanceId);
  }, [openTaskCard]);

  // Actions bridge
  useEffect(() => {
    window.__gauntletActions = {
      start,
      reset,
      setSpeed: setSpeedAction,
      openTaskCard,
    };
    writeGauntletState({
      canvasReady: window.__gauntletState?.canvasReady ?? false,
      running: runningRef.current,
      seed,
      lanes: hudRef.current,
      physicsBodyCount: physicsRef.current?.tumblerBodies.length ?? 0,
      awakeCount: physicsRef.current?.awakeCount ?? 0,
      speed: speedRef.current,
      taskCardOpen: taskCard ? taskCard.id : false,
    });
    return () => {
      delete window.__gauntletActions;
      delete window.__gauntletState;
      if (resultsTimerRef.current) {
        clearTimeout(resultsTimerRef.current);
        resultsTimerRef.current = null;
      }
    };
  }, [start, reset, setSpeedAction, openTaskCard, seed, taskCard]);

  const toggleLaneSlot = (slot: number, newId: string) => {
    setLaneIds((prev) => {
      const next = [...prev];
      const existing = next.indexOf(newId);
      if (existing >= 0 && existing !== slot) {
        next[existing] = next[slot] ?? newId;
      }
      next[slot] = newId;
      return next.slice(0, solAdded ? 4 : MAX_LANES);
    });
    setRunning(false);
    runningRef.current = false;
    setResetKey((k) => k + 1);
  };

  const addSolChallenger = () => {
    setSolAdded(true);
    setLaneIds((prev) => {
      if (prev.includes(SOL_LANE_ID)) return prev;
      // Keep up to 3 verified + Sol as 4th
      const base = prev.filter((id) => id !== SOL_LANE_ID).slice(0, MAX_LANES);
      return [...base, SOL_LANE_ID];
    });
    setResetKey((k) => k + 1);
    setRunCompleted(true);
  };

  const tiers = aleData.tiers;
  const benchmark = aleData.benchmark;
  const failureModes = aleData.failureModes as Array<{
    name: string;
    description: string;
    share: number | null;
  }>;
  const caveats = (aleData.meta as { caveats: string[] }).caveats;
  const prematureFail = failureModes.find((f) =>
    f.name.toLowerCase().includes('premature'),
  );

  const bestLastExam = useMemo(
    () => ALL_VERIFIED.reduce((max, e) => (e.lastExam != null && e.lastExam > max ? e.lastExam : max), 0),
    [],
  );

  const tbScore = aleData.contrast.terminalBench.score;
  const mainLaneCount = hudLanes.length;

  return (
    <div className="gauntlet-page">
      <Canvas
        shadows
        camera={{ position: [0.5, 10.5, 21], fov: 40 }}
        gl={{
          antialias: true,
          alpha: false,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.18,
        }}
      >
        <GauntletScene
          lanes={lanes}
          runningRef={runningRef}
          speedRef={speedRef}
          physicsRef={physicsRef}
          showGotcha={showGotcha}
          onCounts={onCounts}
          onCanvasReady={handleCanvasReady}
          onPhysicsStats={handlePhysicsStats}
          onTumblerClick={onTumblerClick}
          tumblerMeshRefs={tumblerMeshRefs}
        />
      </Canvas>

      <div className="ga-overlay">
        <header className="ga-brand">
          <button className="ga-exit" type="button" onClick={onBack}>
            Exit
          </button>
          <h1 className="ga-title">The Gauntlet</h1>
          <div className="ga-title-rule" />
          <p className="ga-subtitle">Agents&apos; Last Exam</p>
        </header>

        {/* Per-lane HUD */}
        <aside className="ga-lanes" data-testid="gauntlet-lanes">
          {hudLanes.map((lane, i) => {
            const entry = findEntry(lane.id);
            const isSol = entry?.isSol || lane.id === SOL_LANE_ID;
            return (
              <div
                key={lane.id}
                className={`ga-lane-card ${isSol ? 'ga-lane-sol' : ''}`}
                data-testid={`gauntlet-lane-${i}`}
                style={{ borderColor: EXT_LANE_COLORS[i % EXT_LANE_COLORS.length] }}
              >
                <div
                  className="ga-lane-swatch"
                  style={{ background: EXT_LANE_COLORS[i % EXT_LANE_COLORS.length] }}
                />
                <div className="ga-lane-head">
                  <span className="ga-lane-agent">{lane.agent}</span>
                  <span className="ga-lane-backbone">{lane.backbone}</span>
                </div>
                {isSol && (
                  <div className="ga-sol-badge" title={SOL_ENTRY.note ?? ''}>
                    press-reported · unverified
                  </div>
                )}
                <div className="ga-lane-alive">
                  <span className="ga-alive-num">{lane.alive}</span>
                  <span className="ga-alive-label">alive</span>
                </div>
                <div className="ga-lane-elim">
                  <span title="Near-Term eliminated">S1 −{lane.eliminated[0]}</span>
                  <span title="Full-Spectrum eliminated">S2 −{lane.eliminated[1]}</span>
                  <span title="Last-Exam eliminated">S3 −{lane.eliminated[2]}</span>
                </div>
                {lane.falseSummits > 0 && (
                  <div
                    className="ga-lane-fs"
                    title={
                      prematureFail?.description ??
                      "Agents' #1 failure mode: declaring victory on broken work"
                    }
                  >
                    {lane.falseSummits} false summits
                  </div>
                )}
                {lane.clinging > 0 && (
                  <div className="ga-lane-cling">{lane.clinging} clinging</div>
                )}
                {isSol && (
                  <div className="ga-sol-tiers">
                    <span>NT {formatRate(entry?.nearTerm)}</span>
                    <span>FS {formatRate(entry?.fullSpectrum)}</span>
                    <span>LE {formatRate(entry?.lastExam)}</span>
                    {entry?.nearTerm == null && (
                      <span className="ga-no-data-plaque">no reported data</span>
                    )}
                  </div>
                )}
                {entry?.costPerTaskUsd != null && (
                  <div className="ga-lane-cost">
                    ${entry.costPerTaskUsd.toFixed(2)}/task
                  </div>
                )}
              </div>
            );
          })}
        </aside>

        {/* Stage plaques — kept clear of right HUD column */}
        <div className="ga-plaques" aria-hidden="false">
          {tiers.map((tier, i) => (
            <div key={tier.id} className={`ga-plaque ga-plaque-${i}`}>
              <span className="ga-plaque-name">{tier.label}</span>
              <span className="ga-plaque-meta">
                {tier.taskCount} tasks · {formatTimeScale(tier.humanTimeScale)}
              </span>
            </div>
          ))}
          <div className="ga-plaque ga-plaque-summit">
            <span className="ga-plaque-name">Summit</span>
            <span className="ga-plaque-meta">Best ever: {bestLastExam}%</span>
          </div>
          {showGotcha && (
            <div className="ga-plaque ga-plaque-gotcha">
              <span className="ga-plaque-name">Terminal-Bench</span>
              <span className="ga-plaque-meta">{tbScore}% pass · same model</span>
            </div>
          )}
        </div>

        {showGotcha && (
          <div className="ga-course-labels">
            <span className="ga-course-label ga-course-label-main">ALE main course</span>
            <span className="ga-course-label ga-course-label-tb">Terminal-Bench side-course</span>
          </div>
        )}

        {/* Controls */}
        <div className="ga-controls">
          <div className="ga-pickers">
            {Array.from({ length: mainLaneCount || MAX_LANES }, (_, slot) => {
              const isSolSlot = laneIds[slot] === SOL_LANE_ID;
              return (
                <label key={slot} className={`ga-picker ${isSolSlot ? 'ga-picker-sol' : ''}`}>
                  <span
                    className="ga-picker-label"
                    style={{ color: EXT_LANE_COLORS[slot % EXT_LANE_COLORS.length] }}
                  >
                    Lane {slot + 1}
                    {isSolSlot ? ' · SOL' : ''}
                  </span>
                  {isSolSlot ? (
                    <select disabled value={SOL_LANE_ID}>
                      <option value={SOL_LANE_ID}>GPT-5.6 Sol / code mode</option>
                    </select>
                  ) : (
                    <select
                      data-testid={`gauntlet-picker-${slot}`}
                      value={laneIds[slot] ?? ''}
                      disabled={running}
                      onChange={(e) => toggleLaneSlot(slot, e.target.value)}
                    >
                      {ALL_VERIFIED.map((e) => {
                        const id = laneId(e);
                        return (
                          <option key={id} value={id}>
                            {e.agent} / {e.backbone}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </label>
              );
            })}
          </div>

          <div className="ga-buttons">
            <button
              type="button"
              className="ga-btn ga-btn-run"
              data-testid="gauntlet-run"
              disabled={running || laneIds.length === 0}
              onClick={() => start()}
            >
              Run
            </button>
            <button type="button" className="ga-btn" data-testid="gauntlet-reset" onClick={reset}>
              Reset
            </button>
            <div className="ga-speed" role="group" aria-label="Simulation speed">
              {([1, 2, 4] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`ga-speed-btn ${speed === n ? 'active' : ''}`}
                  data-testid={`gauntlet-speed-${n}`}
                  onClick={() => setSpeedAction(n)}
                >
                  {n}×
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`ga-btn ga-btn-gotcha ${showGotcha ? 'active' : ''}`}
              data-testid="gauntlet-gotcha"
              onClick={() => setShowGotcha((v) => !v)}
              title="Same model on Terminal-Bench vs ALE"
            >
              The Gotcha
            </button>
            {runCompleted && !solAdded && (
              <button
                type="button"
                className="ga-btn ga-btn-sol"
                data-testid="gauntlet-sol"
                onClick={addSolChallenger}
              >
                New Challenger
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Task card overlay */}
      {taskCard && (
        <div className="ga-task-card" data-testid="gauntlet-task-card">
          <button
            type="button"
            className="ga-task-close"
            onClick={() => {
              setTaskCard(null);
              writeGauntletState({ taskCardOpen: false });
            }}
          >
            ×
          </button>
          <div className="ga-task-domain">{taskCard.domain.replace(/_/g, ' ')}</div>
          <h3 className="ga-task-title">{taskCard.title}</h3>
          {taskCard.software && (
            <span className="ga-task-soft">{taskCard.software}</span>
          )}
          <p className="ga-task-desc">{taskCard.description}</p>
          {taskCard.humanTime && (
            <div className="ga-task-time">Human time: {taskCard.humanTime}</div>
          )}
          <a
            className="ga-task-link"
            href={taskCard.source}
            target="_blank"
            rel="noreferrer"
          >
            Source
          </a>
        </div>
      )}

      {/* Results panel */}
      {showResults && !running && (
        <div className="ga-results" data-testid="gauntlet-results">
          <div className="ga-results-card">
            <h2>Run results</h2>
            <p className="ga-results-sub">
              Final pit counts vs official leaderboard full-pass rates
            </p>
            <table className="ga-results-table">
              <thead>
                <tr>
                  <th>Config</th>
                  <th>S1 elim</th>
                  <th>NT%</th>
                  <th>S2 elim</th>
                  <th>FS%</th>
                  <th>S3 elim</th>
                  <th>LE%</th>
                  <th>FS flags</th>
                </tr>
              </thead>
              <tbody>
                {hudLanes.map((lane) => {
                  const entry = findEntry(lane.id);
                  return (
                    <tr key={lane.id}>
                      <td>
                        {lane.agent}
                        <br />
                        <small>{lane.backbone}</small>
                      </td>
                      <td>{lane.eliminated[0]}</td>
                      <td>{formatRate(entry?.nearTerm)}</td>
                      <td>{lane.eliminated[1]}</td>
                      <td>{formatRate(entry?.fullSpectrum)}</td>
                      <td>{lane.eliminated[2]}</td>
                      <td>{formatRate(entry?.lastExam)}</td>
                      <td>{lane.falseSummits}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <h3>Top failure modes</h3>
            <ul className="ga-fail-list">
              {failureModes.slice(0, 5).map((fm) => (
                <li key={fm.name}>
                  <strong>{fm.name}</strong>
                  {fm.share != null ? ` (${Math.round(fm.share * 100)}%)` : ''}
                  <span> — {fm.description}</span>
                </li>
              ))}
            </ul>

            <div className="ga-results-actions">
              <button
                type="button"
                className="ga-btn"
                data-testid="gauntlet-caveats"
                onClick={() => setShowCaveats(true)}
              >
                Data &amp; caveats
              </button>
              <button
                type="button"
                className="ga-btn ga-btn-run"
                onClick={() => setShowResults(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Caveats overlay */}
      {showCaveats && (
        <div className="ga-intro" data-testid="gauntlet-caveats-panel">
          <div className="ga-intro-card">
            <h2>Data &amp; caveats</h2>
            <ul className="ga-caveat-list">
              {caveats.map((c) => (
                <li key={c.slice(0, 48)}>{c}</li>
              ))}
            </ul>
            <div className="ga-intro-sources">
              {benchmark.urls.slice(0, 4).map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              ))}
            </div>
            <button
              type="button"
              className="ga-btn ga-btn-run"
              onClick={() => setShowCaveats(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Intro overlay */}
      {showIntro && (
        <div className="ga-intro" data-testid="gauntlet-intro">
          <div className="ga-intro-card">
            <h2>{benchmark.fullName}</h2>
            <p>
              {benchmark.fullName} ({benchmark.name}) is a living benchmark from {benchmark.author}{' '}
              for Generalist Computer-Use Agents on long-horizon professional workflows with
              verifiable outcomes — {benchmark.taskCountTotal.toLocaleString()} tasks across{' '}
              {benchmark.subfields} subfields.
            </p>
            <p>
              Three difficulty tiers separate near-term solvable work from the frontier cliff:
              agents that look strong on easier exams still pile up at the Last-Exam wall.
            </p>
            <p>
              Watch {BOTS_PER_LANE} bots per agent storm the course. Real full-pass rates decide who
              survives each verifier gate — the pits are the statistics.
            </p>

            <table className="ga-tier-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Tasks</th>
                  <th>Human time</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => (
                  <tr key={tier.id}>
                    <td>{tier.label}</td>
                    <td>{tier.taskCount}</td>
                    <td>{formatTimeScale(tier.humanTimeScale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="ga-intro-sources">
              {benchmark.urls.slice(0, 4).map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              ))}
            </div>

            <button
              type="button"
              className="ga-btn ga-btn-run"
              data-testid="gauntlet-intro-dismiss"
              onClick={() => setShowIntro(false)}
            >
              Enter the course
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gauntlet;
